<template>
  <hic-dialog append-to-body :close-on-click-modal="false" :visible="visible" width="35rem" @open="open" title="排班调整" @close="cancel">
    <el-form :model="entity" :rules="rules" ref="editor" label-width="6rem" v-loading="initializing">
      <el-form-item label="人员信息">
        <span>{{ staffName }} ({{ date }})</span>
      </el-form-item>
      
      <el-form-item label="调整方式">
        <el-radio-group v-model="entity.assignType" @change="onTypeChange">
          <el-radio label="series">调整系列</el-radio>
          <el-radio label="single">调整一天</el-radio>
        </el-radio-group>
      </el-form-item>
      
      <el-form-item label="时间长度" v-if="entity.assignType === 'series'" prop="durationMonths">
        <el-select v-model="entity.durationMonths" placeholder="请选择排班时长" style="width: 100%">
          <el-option v-for="moon in 6" :key="moon" :label="moon + ' 个月'" :value="moon"></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="选择系列" v-if="entity.assignType === 'series'" prop="seriesId">
        <el-select v-model="entity.seriesId" placeholder="请选择班次系列" style="width: 100%" @change="onSeriesChange">
          <el-option v-for="s in availableSeries" :key="s.id" :label="s.name" :value="s.id"></el-option>
        </el-select>
        
        <!-- 展示选中系列包含的班次 -->
        <div v-if="selectedSeriesItems.length > 0" style="margin-top: 10px; line-height: 24px;">
          <div style="font-size: 12px; color: #606266; margin-bottom: 5px;">请点击选择从哪一个班次开始排班：</div>
          <span 
            v-for="(item, idx) in selectedSeriesItems" 
            :key="idx" 
            class="series-shift-tag"
            :class="{ active: entity.seriesOffset === idx }"
            :style="{ backgroundColor: getShiftColor(item.shiftTypeId), borderColor: entity.seriesOffset === idx ? '#409EFF' : 'transparent' }"
            @click="entity.seriesOffset = idx"
          >
            {{ getShiftName(item.shiftTypeId) }}
          </span>
        </div>
      </el-form-item>
      
      <el-form-item label="选择班次" v-if="entity.assignType === 'single'" prop="shiftTypeId">
        <el-select v-model="entity.shiftTypeId" placeholder="请选择班次" style="width: 100%" clearable>
          <el-option v-for="s in shiftTypes" :key="s.id" :label="s.name" :value="s.id">
            <span style="float: left">{{ s.name }}</span>
            <span style="float: right; width: 15px; height: 15px; border-radius: 2px; margin-top: 9px;" :style="{ backgroundColor: s.color }"></span>
          </el-option>
        </el-select>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="cancel">取 消</el-button>
      <el-button type="primary" @click="save" :loading="saving">确 定</el-button>
    </div>
  </hic-dialog>
</template>

<script>
import api from '@/common/api'

export default {
  props: {
    visible: Boolean,
    staffId: { type: Number, default: null },
    staffName: String,
    staffTypeCode: String,
    date: String,
    currentShiftTypeId: Number,
    shiftTypes: Array,
    seriesList: Array
  },
  data() {
    return {
      initializing: false,
      saving: false,
      entity: {
        assignType: 'series',
        seriesId: null,
        seriesOffset: 0,
        shiftTypeId: null,
        durationMonths: 1
      },
      rules: {
        seriesId: [
          { required: true, message: '请选择班次系列', trigger: 'change' }
        ],
        shiftTypeId: [
          { required: true, message: '请选择班次', trigger: 'change' }
        ],
        durationMonths: [
          { required: true, message: '请选择排班时间长度', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    availableSeries() {
      if (!this.staffTypeCode) return this.seriesList
      // 根据当前员工类型过滤系列
      const seriesList = this.seriesList.filter(s => s.staffTypeCode === this.staffTypeCode)
      return seriesList.length ? seriesList : this.seriesList
    },
    selectedSeriesItems() {
      if (!this.entity.seriesId) return []
      const series = this.seriesList.find(s => s.id === this.entity.seriesId)
      return series ? (series.items || []) : []
    }
  },
  methods: {
    getShiftName(shiftTypeId) {
      const shift = this.shiftTypes.find(s => s.id === shiftTypeId)
      return shift ? shift.name : '未知'
    },
    getShiftColor(shiftTypeId) {
      const shift = this.shiftTypes.find(s => s.id === shiftTypeId)
      return shift ? shift.color : '#eee'
    },
    async open() {
      // 重置表单初始值
      this.entity.assignType = 'series'
      this.entity.seriesId = this.availableSeries.length > 0 ? this.availableSeries[0].id : null
      this.entity.seriesOffset = 0
      this.entity.shiftTypeId = this.currentShiftTypeId || null
      this.entity.durationMonths = 1

      if (this.$refs.editor) {
        this.$refs.editor.clearValidate()
      }

      // 若没有员工 ID 或日期，不做预填处理
      if (!this.staffId || !this.date) return

      try {
        this.initializing = true
        // 调用专用接口，直接返回该员工在 date 之前最近一次排班记录
        const lastEntry = await api.Schedule.lastEntry(this.staffId, this.date)
        if (!lastEntry || !lastEntry.shiftTypeId) return  // 无历史排班，保持默认值

        // 在可用系列中查找包含该班次的系列
        const pool = this.availableSeries.length ? this.availableSeries : this.seriesList
        const matchSeries = pool.find(
          s => s.items && s.items.some(item => item.shiftTypeId === lastEntry.shiftTypeId)
        )

        if (!matchSeries) return  // 找不到匹配系列，保持默认值

        const items = matchSeries.items || []
        const lastIdx = items.findIndex(item => item.shiftTypeId === lastEntry.shiftTypeId)

        this.entity.seriesId = matchSeries.id
        // 起始班次取最近一次班次的下一个，循环取余
        this.entity.seriesOffset = lastIdx >= 0 ? (lastIdx + 1) % items.length : 0
      } catch (e) {
        console.warn('获取最近排班记录失败', e)
      } finally {
        this.initializing = false
      }
    },
    onTypeChange() {
      if (this.$refs.editor) {
        this.$refs.editor.clearValidate()
      }
    },
    onSeriesChange() {
      // 切换系列时，重置选中的偏移量为 0（第一个班次）
      this.entity.seriesOffset = 0
    },
    cancel() {
      this.$emit('update:visible', false)
    },
    save() {
      this.$refs.editor.validate((valid) => {
        if (valid) {
          this.$emit('ok', {
             assignType: this.entity.assignType,
             seriesId: this.entity.seriesId,
             seriesOffset: this.entity.seriesOffset,
             shiftTypeId: this.entity.shiftTypeId,
             durationMonths: this.entity.durationMonths,
             date: this.date
          })
          this.$emit('update:visible', false)
        }
      })
    }
  }
}
</script>

<style scoped>
.series-shift-tag {
  display: inline-block;
  padding: 2px 8px;
  margin-right: 8px;
  margin-bottom: 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
  user-select: none;
}
.series-shift-tag:hover {
  filter: brightness(0.9);
}
.series-shift-tag.active {
  border-color: #409EFF !important;
  box-shadow: 0 0 3px rgba(64,158,255,0.5);
  font-weight: bold;
}
</style>
